import { put, call, delay, select } from 'redux-saga/effects';
import { overtimeConstants } from './OvertimeConstants';
import { getCallsheet, addOTShift, resolveWbFailed } from './index';
import { getOTSkillsData } from '../OT-Comp/index';
import * as actions from '../OT-Comp/OvertimeAction';
import { getStationTimestamp } from '../VTO/utils';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export function* getOvertimeData(action) {
    try {
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: true });
        const response = yield call(getCallsheet, action.params);
        
        yield put({ 
            type: overtimeConstants.GET_OVERTIME_DATA_SUCCESS, 
            data: response.data,
            requestParams: action.params
        });
        
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
       
    } catch (error) {
        yield put({ 
            type: overtimeConstants.GET_OVERTIME_DATA_FAILED, 
            data: error.message || 'Failed to fetch overtime data' 
        });
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
     
    }
}

export function* confirmOvertimeDecision(action) {
    const { employeeId, shiftName, decision, shiftData } = action;
    for(let i = 0; i<3; i++) {
        try {
            yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: true });

                const requestBody = {
                    empId: employeeId,
                    votRequestId: shiftData?.votRequestId || '',
                    station: shiftData?.station || '',
                    shiftDate: shiftData?.shiftDate || '',
                    otStartTime: shiftData?.otStartTime || '',
                    otEndTime: shiftData?.otEndTime || '',
                    shiftType: shiftData?.shiftType || '',
                    workLocation: shiftData?.workLocation || '',
                    schedBidGroup: shiftData?.schedBidGroup || '',
                    otGroup: shiftData?.otGroup || '',
                    unpaidLunch: shiftData?.unpaidLunch || 'Y', //TEMP
                    overlapAllowed: shiftData?.overlapAllowed || 'Y', //TEMP
                    requestType: decision == "ACCEPT" ? 'APPLY' : decision,
                    reason: shiftData?.reason || 'Vac. coverage', //TEMP
                    previousTransactionId: shiftData?.previousTransactionId || '',
                    transactionTime: shiftData?.transactionTime || new Date().toISOString()
                };

                const response = yield call(addOTShift, requestBody);
                if (response?.status === 200 || response?.data?.statusCode === 'SUCCESS' || response?.data?.result?.status === 'SUCCESS') {
                    const station = shiftData?.station || '';
                    let lastTimeStamp = '';
                    try {
                        lastTimeStamp = yield call(getStationTimestamp, station);
                    } catch (e) {
                        lastTimeStamp = new Date().toISOString();
                    }
                    yield put({
                        type: overtimeConstants.CONFIRM_DECISION_SUCCESS,
                        employeeId,
                        shiftName,
                        decision,
                        lastTimeStamp
                    });
                } else {
                    throw new Error(response?.data?.message || 'Failed to assign shift. Please try again.');
                }

            yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
            break;
        } catch (error) {
            yield put({
                type: overtimeConstants.CONFIRM_DECISION_FAILED,
                data: error.message || 'Failed to confirm decision. Please try again.'
            });
            
            yield put({
                type: overtimeConstants.ADD_OT_SHIFT_FAILED,
                employeeId,
                shiftName,
                data: error.message || 'Failed to assign shift. Please try again.'
            });
            
            yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
            break;
        }
    }
}

export function* resolveOTWbFailedSaga(action) {
    const { employeeId, shiftName, resolution } = action;
    try {
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: true });

        const response = yield call(resolveWbFailed, {
            offerId: resolution.offerId,
            employeeId: resolution.employeeId,
            workbrainRequestId: resolution.workbrainRequestId,
            resolveAction: resolution.resolveAction,
            resolveComments: resolution.resolveComments,
        });

        if (response?.status === 200) {
            yield put({
                type: overtimeConstants.RESOLVE_OT_WB_FAILED_SUCCESS,
                employeeId,
                shiftName,
                resolveAction: resolution.resolveAction,
            });
        } else {
            yield put({
                type: overtimeConstants.RESOLVE_OT_WB_FAILED_FAILED,
                employeeId,
                shiftName,
                data: response?.data?.message || 'Failed to resolve WB Failed request.',
            });
        }

        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
    } catch (error) {
        yield put({
            type: overtimeConstants.RESOLVE_OT_WB_FAILED_FAILED,
            employeeId,
            shiftName,
            data: error.message || 'Failed to resolve WB Failed request.',
        });
        yield put({ type: overtimeConstants.SHOW_OVERTIME_LOADER, data: false });
    }
}

export function* getOTSkillsDataSaga(data) {
    for(let i = 0; i < MAX_RETRIES; i++) {
        try {
            const response = yield call(
                getOTSkillsData,
                data.params,
            )
            
            if (response?.status === 200) {
                yield put(actions.getOTSkillsDataSuccess(true))
                const skillsData = response.data?.otskills || response.data?.skills || response.data || [];
                yield put(actions.getOTSkillsResponse(skillsData))
                return; // Success - exit the function
            } else {
                console.log("ERROR: " + response?.status);
                if (i === MAX_RETRIES - 1) {
                    yield put(actions.getOTSkillsDataSuccess(false))
                } else {
                    yield delay(RETRY_DELAY);
                }
            }
        }
        catch (error) {
            console.log("ERROR: " + error);
            if (i === MAX_RETRIES - 1) {
                // Last retry attempt failed
                yield put(actions.getOTSkillsDataSuccess(false))
            } else {
                yield delay(RETRY_DELAY);
            }
        }
    }
}

export function* advanceToNextRequirementSaga() {
    try {
        const callSheetData = yield select((state) => state.OvertimeReducer.callSheetData);
        const requestParams = callSheetData?.requestParams;
        const currentShift = callSheetData?.callSheetWorkingShift?.current || '';

        if (!requestParams || !currentShift) {
            return;
        }

        // Filter out the fulfilled requirement by matching shiftType to current shift name
        const updatedShiftsReqs = (requestParams.shiftsReqs || []).filter(
            (req) => (req.shiftType || '').toUpperCase() !== currentShift.toUpperCase()
        );

        const updatedParams = {
            ...requestParams,
            shiftsReqs: updatedShiftsReqs
        };

        // Reset fulfillment flag before fetching
        yield put({ type: overtimeConstants.RESET_SLOT_FULFILLED });

        // Re-fetch callsheet with remaining requirements
        yield put({ type: overtimeConstants.GET_OVERTIME_DATA, params: updatedParams });
    } catch (error) {
        console.error('Error advancing to next requirement:', error);
    }
}
